<template>
  <div>
    <CommunityMapFilters
      v-if="false"
      :floor-plans="floorPlans"
      :property-types="propertyTypes"
      :school-districts="schoolDistricts"
      :results-count="communities.length"
    />

    <div class="list" :class="{ 'is-mobile': windowWidth < 900, 'is-full': !isMapDisplayed, 'is-full-triggered': !isMapDisplayed }">
      <CommunityListDrawer />
      <MapboxMap
        v-show="isMapDisplayed"
        class="map"
        :access-token="accessToken"
        :map-style="mapStyle"
        :center="coordinates"
        :zoom="9"
        :antialias="true"
        :pitch-with-rotate="false"
        @mb-created="(mapboxInstance) => (map = mapboxInstance)"
      >
        <button v-if="windowWidth < 900" type="button" class="map-close" @click="closeMap">
          <IconClose />
        </button>
        <div class="map-poi">
          <ToggleButton
            class="map-poi-switch"
            :color="{ checked: '#000000', unchecked: '#9F8054', disabled: '#CCCCCC' }"
            :width="34"
            :height="22"
            @change="togglePoi"
          />
          Points of Interest
        </div>

        <template v-for="c in communitiesWithCoordinates">
          <MapboxMarker
            v-if="c.coordinates"
            :lng-lat="c.coordinates"
            :ref="`mapbox-marker-${c.id}`"
            :key="`mapbox-marker-${c.id}`"
          >
            <div class="marker-icon"></div>
          </MapboxMarker>
          <MapboxPopup
            v-if="c.coordinates"
            :lng-lat="c.coordinates"
            :close-button="false"
            :close-on-click="false"
            :offset="12"
            :class-name="`mapbox-popup-${c.id}`"
            :ref="`mapbox-popup-${c.id}`"
            :key="`mapbox-popup-${c.id}`"
          >
            <div class="popup-thumbnail">
              <img :src="c.slide_images[0].url" :alt="c.slide_images[0].title" />
            </div>
            <div class="popup-price">{{ startingAt(c.price) }}</div>
            <div class="popup-title">{{ c.title }}</div>
          </MapboxPopup>
        </template>

        <template v-if="showPointsOfInterest">
          <MapboxMarker
            v-for="poi in pointsOfInterest"
            :lng-lat="[poi.address.lng, poi.address.lat]"
            :ref="`mapbox-marker-poi-${poi.id}`"
            :key="`mapbox-marker-poi-${poi.id}`"
            :popup="{
              'lngLat': [poi.address.lng, poi.address.lat],
              'closeButton': false,
              'closeOnClick': true,
              'offset': 12,
              'className': `mapbox-popup-${poi.id}`,
            }"
          >
            <component :is="`icon-poi-${poi.poi_type.value}`" />
            <template v-slot:popup>
              <div class="popup-title">{{ poi.title }}</div>
            </template>
          </MapboxMarker>
        </template>

        <MapboxNavigationControl position="bottom-right" />
      </MapboxMap>
    </div>
  </div>
</template>

<script>
import * as _ from 'lodash';
import geocodingClient from '@mapbox/mapbox-sdk/services/geocoding';
import MapboxMap from '@studiometa/vue-mapbox-gl/dist/components/MapboxMap';
import MapboxMarker from '@studiometa/vue-mapbox-gl/dist/components/MapboxMarker';
import MapboxNavigationControl from '@studiometa/vue-mapbox-gl/dist/components/MapboxNavigationControl';
import MapboxPopup from '@studiometa/vue-mapbox-gl/dist/components/MapboxPopup';
import { ToggleButton } from 'vue-js-toggle-button';

import CommunityMapFilters from '@/components/filters/CommunityMapFilters';
import CommunityListDrawer from '@/components/CommunityListDrawer';
import IconClose from '@/components/icons/IconClose';
import IconPoiGas from '@/components/icons/IconPoiGas';
import IconPoiCoffee from '@/components/icons/IconPoiCoffee';
import IconPoiDaycare from '@/components/icons/IconPoiDaycare';
import IconPoiDogPark from '@/components/icons/IconPoiDogPark';
import IconPoiEducation from '@/components/icons/IconPoiEducation';
import IconPoiGrocery from '@/components/icons/IconPoiGrocery';
import IconPoiGym from '@/components/icons/IconPoiGym';
import IconPoiMedical from '@/components/icons/IconPoiMedical';
import IconPoiPark from '@/components/icons/IconPoiPark';
import IconPoiRestaurant from '@/components/icons/IconPoiRestaurant';
import IconPoiShopping from '@/components/icons/IconPoiShopping';
import IconPoiAirport from '@/components/icons/IconPoiAirport';
import IconPoiEntertainment from '@/components/icons/IconPoiEntertainment';
import IconPoiGolf from '@/components/icons/IconPoiGolf';
import IconPoiWinery from '@/components/icons/IconPoiWinery';
import IconPoiTrain from '@/components/icons/IconPoiTrain';
import IconPoiCourthouse from '@/components/icons/IconPoiCourthouse';

export default {
  name: 'CommunitiesMap',
  components: {
    IconClose,
    IconPoiCoffee,
    IconPoiDaycare,
    IconPoiDogPark,
    IconPoiEducation,
    IconPoiGas,
    IconPoiGrocery,
    IconPoiGym,
    IconPoiMedical,
    IconPoiPark,
    IconPoiRestaurant,
    IconPoiShopping,
    IconPoiAirport,
    IconPoiEntertainment,
    IconPoiGolf,
    IconPoiWinery,
    IconPoiTrain,
    IconPoiCourthouse,
    CommunityListDrawer,
    CommunityMapFilters,
    MapboxMap,
    MapboxMarker,
    MapboxNavigationControl,
    MapboxPopup,
    ToggleButton,
  },
  props: {
    city: {
      required: true,
      type: String,
    },
  },
  data() {
    const accessToken = window.atmoMaps.mapbox_api_key;
    const mapboxGeoCodingClient = geocodingClient({ accessToken });

    const cityCoordinates = {
      pittsburgh: [-79.9959, 40.4406],
      philadelphia: [-75.1652, 39.9526],
    };

    return {
      accessToken,
      mapboxGeoCodingClient,
      mapStyle: 'mapbox://styles/atmo/cl7s4bi4d004414uq9rbkbf3x',
      map: null,
      popupObserver: null,
      coordinates: cityCoordinates[this.city],
      showPointsOfInterest: false,
      pointsOfInterest: [],
      windowWidth: window.innerWidth,
    };
  },
  computed: {
    communities() {
      // console.log('CommunitiesMap communities()');
      return this.$store.getters['communities/getCommunities'];
    },
    communitiesAll() {
      // console.log('CommunitiesMap communitiesAll()');
      return this.$store.getters['communities/getCommunitiesAll'];
    },
    communitiesWithCoordinates() {
      // console.log('CommunitiesMap communitiesWithCoordinates() filter');
      return this.communities.filter((community) => {
        return Array.isArray(community.coordinates);
      });
    },
    community() {
      return this.$store.getters['ui/getDetailedCommunity'];
    },
    filters() {
      return this.$store.getters['communities/getFilters'];
    },
    floorPlans() {
      return this.$store.getters['floorplans/getFloorplans'].map((f) => f.title).sort();
    },
    isDetailedView() {
      // console.log('CommunitiesMap isDetailedView()', this.$store.getters['ui/getPanelView']);
      return this.$store.getters['ui/getPanelView'] === 'detail';
    },
    isMapDisplayed() {
      // console.log('CommunitiesMap isMapDisplayed()', this.$store.getters['ui/isMapDisplayed']);
      return this.$store.getters['ui/isMapDisplayed'];
    },
    propertyTypes() {
      return _.uniq(this.communitiesAll.map((c) => c.property_type).sort());
    },
    schoolDistricts() {
      return _.uniq(this.communitiesAll.map((c) => c.school_district).sort());
    },
    zoomToLocation() {
      return this.$store.getters['ui/getZoomToLocation'];
    },
  },
  watch: {
    map: function(newValue) {
      // Set up the MutationObserver whenever the map instance is assigned or replaced
      if (this.popupObserver) this.popupObserver.disconnect();
      if (newValue) this.setupPopupObserver();
    },
    community: function(newValue, oldValue) {
      // console.log('CommunitiesMap watch community', newValue, oldValue, this.isDetailedView);
      if (oldValue !== false && this.isDetailedView === true) {
        this.showPopupDetail();
      }
    },
    communitiesAll: function(newValue, oldValue) {
      // console.log('CommunitiesMap watch communities', newValue.length, oldValue.length);
      if (newValue.length > oldValue.length) {
        this.updateCommunityCoordinates();
      }
    },
    isDetailedView: function(newValue, oldValue) {
      // console.log('CommunitiesMap watch isDetailedView', newValue, oldValue);
      if (oldValue === false && newValue === true) {
        this.showPopupDetail();
      } else {
        this.clearPopups();
      }
    },
    zoomToLocation: function(newValue, oldValue) {
      // console.log('CommunitiesMap watch zoomToLocation', newValue, oldValue);
      // new is not false, and its a change from previous value
      if (newValue !== false && newValue !== oldValue) {
        this.fitLocationInBounds(newValue);
      }
    },
  },
  mounted() {
    if (window.innerWidth < 900) {
      this.$store.dispatch('ui/setMapDisplay', { mapDisplay: false });
    }
    window.addEventListener('resize', this.handleResize);
    this.fetchData();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.popupObserver) this.popupObserver.disconnect();
  },
  methods: {
    handleResize: _.throttle(function() {
      // console.log('CommunitiesMap handleResize()', window.innerWidth);
      this.windowWidth = window.innerWidth;
      this.$store.dispatch('ui/setMapDisplay', { mapDisplay: this.windowWidth >= 900 });
    }, 250),
    clearPopups() {
      const popups = document.getElementsByClassName('mapboxgl-popup');
      popups.forEach((el) => {
        // dim other popups
        el.classList.remove('mapboxgl-popup-front');
        el.classList.remove('mapboxgl-popup-highlight');
      });
    },
    closeMap() {
      this.$store.dispatch('ui/setMapDisplay', { mapDisplay: false });
    },
    fetchCommunities() {
      // console.log('CommunitiesMap fetchCommunities()');
      // apply taxonomy or meta queries
      const payload = {
        tax_queries: [
          {
            taxonomy: 'community-area',
            field: 'slug',
            terms: this.city,
          },
        ],
      };
      // get communities from WP
      return this.$store.dispatch('communities/all', payload);
    },
    fetchData() {
      this.fetchCommunities().then(() => {
        // #TODO Cleanup this portion, maybe needs a loading state to make decisions on what to show and when
        // see if there is anything in the url, set as default if there is
        // console.log('CommunitiesMap fetchData() communities then');
        const _this = this;
        setTimeout(function() {
          _this.maybeSetCommunityFromUrl();
        }, 1000);
      });
      this.fetchFloorplans();
      this.fetchPointsOfInterest();
    },
    fetchFloorplans() {
      // apply filters
      const payload = {};
      // get floorplans from WP
      return this.$store.dispatch('floorplans/all', payload);
    },
    fetchPointsOfInterest() {
      // get points of interest from WP
      this.$store
        .dispatch('pointsOfInterest/all')
        .then((response) => {
          this.pointsOfInterest = response.data.posts;
        })
        .catch()
        .finally();
    },
    fitLocationInBounds(location) {
      // get all the communities in this location
      const locationCommunities = this.$store.getters['communities/getCommunitiesByLocation'](location.slug);
      // get the min/max lat and lng values
      const longitudes = locationCommunities.map((lc) => lc.coordinates[0]);
      const latitudes = locationCommunities.map((lc) => lc.coordinates[1]);

      // set fitBounds with padding (this requires a LatLngLike value)
      const bounds = [
        [Math.min(...longitudes), Math.min(...latitudes)],
        [Math.max(...longitudes), Math.max(...latitudes)],
      ];
      const options = {
        padding: 40,
        linear: false,
      };
      this.map.fitBounds(bounds, options);

      // clear zoomToLocation back to default
      this.$store.dispatch('ui/setZoomToLocation', { location: false });
    },
    getCommunityById(id) {
      return this.communities.find((c) => c.id === Number(id));
    },
    getCommunityBySlug(slug) {
      return this.communities.find((c) => c.slug === slug);
    },
    maybeSetCommunityFromUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('community')) {
        // set detail pane if we have a legit community slug
        const community = this.getCommunityBySlug(urlParams.get('community'));
        if (community) {
          this.$store.dispatch('ui/setPanelView', { community, panelView: 'detail' });
        }
      }
    },
    setupPopupObserver() {
      const mapContainer = this.map.getContainer();

      this.popupObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1 && node.classList.contains('mapboxgl-popup')) {
              this.attachPopupListeners(node);
            }
          });
        });
      });

      this.popupObserver.observe(mapContainer, { childList: true, subtree: true });

      // Handle any popups Mapbox may have already added to the container
      mapContainer.querySelectorAll('.mapboxgl-popup').forEach((el) => this.attachPopupListeners(el));
    },
    attachPopupListeners(element) {
      // Skip if this exact DOM element already has listeners attached
      if (element.dataset.listenersRegistered) return;

      // Only handle our community popups (ignore POI popups)
      const idClass = Array.from(element.classList).find((c) => c.startsWith('mapbox-popup-'));
      if (!idClass) return;

      const communityId = idClass.replace('mapbox-popup-', '');
      const popups = document.getElementsByClassName('mapboxgl-popup');

      element.dataset.listenersRegistered = 'true';

      /**
       * When we hover over the popup, bring it to the front
       */
      element.addEventListener('mouseover', () => {
        // console.log('CommunitiesMap attachPopupListeners() on mouseover', communityId);
        Array.from(popups).forEach((el) => el.classList.remove('mapboxgl-popup-front'));
        element.classList.add('mapboxgl-popup-front');
      });

      /**
       * Clicking the popup expands the popup and collapses the others. Then set the current community as the detail
       */
      element.addEventListener('click', () => {
        // console.log('CommunitiesMap attachPopupListeners() on click', communityId);
        const payload = { community: this.getCommunityById(communityId), panelView: 'detail' };
        this.$store.dispatch('ui/setPanelView', payload);
      });
    },
    showPopupDetail() {
      // console.log('CommunitiesMap showPopupDetail()', this.isMapDisplayed, this.community);
      this.clearPopups();

      const element = document
        .getElementsByClassName(`mapbox-popup-${this.community.id}`)[0]
        .closest('.mapboxgl-popup');

      // expand popup
      element.classList.add('mapboxgl-popup-front');
      element.classList.add('mapboxgl-popup-highlight');
    },
    startingAt(price) {
      const thePrice = Number(price.replaceAll(',', ''));
      if (thePrice > 0) {
        const formattedAmount = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0  // Ensures at most two decimal places
        }).format(thePrice);

        return formattedAmount;
      } else {
        return 'Contact Us';
      }
    },
    togglePoi(toggle) {
      // console.log('CommunitiesMap togglePoi()', toggle);
      // toggle display of POI layer
      this.showPointsOfInterest = toggle.value;
    },
    updateCommunityCoordinates() {
      // console.log('CommunitiesMap updateCommunityCoordinates()');
      this.communities.forEach((community, i) => {
        // console.log('CommunitiesMap updateCommunityCoordinates() each', community.full_address, community.title);
        if (community.full_address) {
          this.mapboxGeoCodingClient
            .forwardGeocode({
              query: community.full_address,
              // limit: 2,
              autocomplete: false,
            })
            .send()
            .then((response) => {
              // console.log('CommunitiesMap updateCommunityCoordinates() each then', community.title, response.body);
              const payload = {
                index: i,
                field: 'coordinates',
                value: response.body.features.find((f) => f.place_type.includes('address'))?.geometry.coordinates,
              };
              this.$store.dispatch('communities/setCommunityAttribute', payload);
            })
            .catch((error) => {
              console.error('CommunitiesMap updateCommunityCoordinates() each catch', error, community.title, community);
            });
        }

        return community;
      });
    },
  },
};
</script>
